/** In-tab audio admission only. No account data, content or native routing. */
export type PriorityAudioKind = "live" | "media" | "speech" | "capture";
export interface AppAudioLease {
  isCurrent(): boolean;
  release(): void;
}
export interface CoachingAudioLease extends AppAudioLease { readonly kind: "coaching" }
export function createAppAudioFocus() {
  const owners = new Map<symbol, PriorityAudioKind>();
  const listeners = new Set<() => void>();
  let coach: { token: symbol; revoke: () => void } | null = null;
  const emit = () => { for (const listener of [...listeners]) listener(); };
  const blocking = (): PriorityAudioKind | null => {
    for (const kind of ["live", "capture", "speech", "media"] as const)
      if ([...owners.values()].includes(kind)) return kind;
    return null;
  };
  return {
    blocking,
    subscribe(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; },
    acquire(kind: PriorityAudioKind): AppAudioLease {
      const token = Symbol(kind);
      owners.set(token, kind);
      // Revoke before any microphone, bridge or network await can begin.
      const previous = coach;
      coach = null;
      previous?.revoke();
      emit();
      return {
        isCurrent: () => owners.has(token),
        release() { if (owners.delete(token)) emit(); },
      };
    },
    tryAcquireCoach(revoke: () => void): CoachingAudioLease | null {
      if (blocking() || coach) return null;
      const token = Symbol("coaching");
      coach = { token, revoke };
      return {
        kind: "coaching",
        isCurrent: () => coach?.token === token && blocking() === null,
        release() { if (coach?.token === token) { coach = null; emit(); } },
      };
    },
  };
}
export const appAudioFocus = createAppAudioFocus();
